import RoleAssociationTab from '../RoleAssociationTab/RoleAssociationTab';
import { isFormConfigured } from '../../../utils/formConfig';
import { QueryKey } from '@tanstack/react-query';
import SingleItem from 'src/layouts/SingleItem';
import { IGetSubmissionEditorialAssigneesResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
// import PermissionsAndClearancesTab from '../PermissionsAndClearancesTab/PermissionsAndClearancesTab';
import CopyrightAndLicensingTab from '../CopyrightAndLicensingTab/CopyrightAndLicensingTab';
import {
  Flex,
  Text,
  Tooltip,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button
} from '@chakra-ui/react';
import { useSubmissionDetails } from 'src/pages/SubmissionDetails/SubmissionDetailsContext';
import { useSubmissionContextData } from '../../../contexts/SubmissionContextProvider';
import ICMJEDisclosureTab from '../ICMJEDisclosureTab/ICMJEDisclosureTab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { ButtonIcon } from '@morressier/alembic';
import VersionHistoryLog from '../VersionHistoryLog';
import VendorExports from 'src/pages/SubmissionDetails/VendorExports';
import { useLocation } from 'react-router-dom';

export const BACKOFFICE_TABS = {
  ROLE_ASSOCIATION: {
    label: 'Associated Roles',
    hasTooltip: true,
    tooltip:
      'Assign editorial roles to manage specific aspects of this Submission.'
  },
  // PERMISSIONS_AND_CLEARANCES: {
  //   label: 'Permissions & Clearances',
  //   hasTooltip: true,

  //   tooltip:
  //     'Manage permissions for any reproduced content used in the Submission, and request additional documentation from the Author if necessary.'
  // },
  COPYRIGHT_AND_LICENSING: {
    label: 'Copyright & Licensing',
    hasTooltip: false
  },
  ICMJE_DISCLOSURE: {
    label: 'Forms and Disclosures',
    hasTooltip: false
  },
  VERSION_HISTORY_LOG: {
    label: 'Version Log',
    hasTooltip: true,
    tooltip:
      'This log displays all files included in each revision round, allowing you to track changes and updates across the entire submission cycle'
  },
  VENDOR_EXPORTS: {
    label: 'Exports',
    hasTooltip: false
  }
} as const;

export type IBackofficeTab =
  (typeof BACKOFFICE_TABS)[keyof typeof BACKOFFICE_TABS];

type RenderTabContent = {
  activeTab: IBackofficeTab;
  queryKey: QueryKey;
  editorialAssignees: IGetSubmissionEditorialAssigneesResponse['editorialAssignees'];
  submissionId: string;
};

export const renderTabContent = ({
  activeTab,
  queryKey,
  editorialAssignees,
  submissionId
}: RenderTabContent) => {
  switch (activeTab) {
    case BACKOFFICE_TABS.ROLE_ASSOCIATION:
      return (
        <RoleAssociationTab
          queryKey={queryKey}
          editorialAssignees={editorialAssignees}
        />
      );
    // case BACKOFFICE_TABS.PERMISSIONS_AND_CLEARANCES:
    //   return <PermissionsAndClearancesTab />;
    case BACKOFFICE_TABS.COPYRIGHT_AND_LICENSING:
      return <CopyrightAndLicensingTab />;
    case BACKOFFICE_TABS.ICMJE_DISCLOSURE:
      return <ICMJEDisclosureTab />;
    case BACKOFFICE_TABS.VERSION_HISTORY_LOG:
      return <VersionHistoryLog submissionId={submissionId} />;
    case BACKOFFICE_TABS.VENDOR_EXPORTS:
      return <VendorExports />;
    default:
      return null;
  }
};

interface Props {
  queryKey: QueryKey;
  editorialAssignees: IGetSubmissionEditorialAssigneesResponse['editorialAssignees'];
  submissionId: string;
}
export type BackofficeTabLabel =
  (typeof BACKOFFICE_TABS)[keyof typeof BACKOFFICE_TABS]['label'];

const BackofficeTab = ({
  queryKey,
  editorialAssignees,
  submissionId
}: Props) => {
  const { submissionContext } = useSubmissionContextData();
  const isCopyrightConfigured = isFormConfigured(
    submissionContext?.config.authorCopyrightModel
  );
  const isICMJEConfigured = isFormConfigured(
    submissionContext?.config.authorICMJEModel
  );
  const cocConfig = submissionContext?.config.cocConfig?.length === 0;
  const { backOfficeSubTab, setBackOfficeSubTab } = useSubmissionDetails();
  const location = useLocation();
  const filteredTabs = cocConfig
    ? [
        BACKOFFICE_TABS.ROLE_ASSOCIATION,
        // BACKOFFICE_TABS.PERMISSIONS_AND_CLEARANCES,
        ...(isCopyrightConfigured
          ? [BACKOFFICE_TABS.COPYRIGHT_AND_LICENSING]
          : []),
        ...(isICMJEConfigured ? [BACKOFFICE_TABS.ICMJE_DISCLOSURE] : []),
        BACKOFFICE_TABS.VERSION_HISTORY_LOG,
        BACKOFFICE_TABS.VENDOR_EXPORTS
      ]
    : [
        // BACKOFFICE_TABS.PERMISSIONS_AND_CLEARANCES,
        ...(isCopyrightConfigured
          ? [BACKOFFICE_TABS.COPYRIGHT_AND_LICENSING]
          : []),
        ...(isICMJEConfigured ? [BACKOFFICE_TABS.ICMJE_DISCLOSURE] : []),
        BACKOFFICE_TABS.VERSION_HISTORY_LOG,
        BACKOFFICE_TABS.VENDOR_EXPORTS
      ];

  const isValidTabLabel = (value: string): value is BackofficeTabLabel => {
    return Object.values(BACKOFFICE_TABS).some(tab => tab.label === value);
  };

  const getTabByLabel = (label: BackofficeTabLabel) => {
    return Object.values(BACKOFFICE_TABS).find(tab => tab.label === label);
  };
  const params = new URLSearchParams(location.search);
  const urlTab = params.get('activeSubTab');

  const defaultTab =
    urlTab && isValidTabLabel(urlTab) ? getTabByLabel(urlTab) : undefined;

  const activeTab: IBackofficeTab = filteredTabs.includes(backOfficeSubTab)
    ? backOfficeSubTab
    : defaultTab && filteredTabs.includes(defaultTab)
    ? defaultTab
    : filteredTabs[0] ?? BACKOFFICE_TABS.ROLE_ASSOCIATION;

  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleSubTabChange = (tab: IBackofficeTab) => {
    setBackOfficeSubTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('activeSubTab', tab.label);
    window.history.replaceState({}, '', `?${params.toString()}`);
  };

  return (
    <Flex flexDirection="column" gap={4}>
      <Flex flexDirection="column" gap={2}>
        <Text textStyle="headingSection">Backoffice</Text>
        <Text textStyle="body">
          Manage roles, permissions, and key information related to this
          submission's administrative process.
        </Text>
      </Flex>
      <SingleItem.MainArea>
        <SingleItem.Navigation size="compact">
          {isMobile ? (
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
                fontWeight="semibold"
                size="sm"
                px={4}
              >
                {activeTab.label}
              </MenuButton>
              <MenuList zIndex={10}>
                {filteredTabs.map(tab => (
                  <MenuItem
                    key={tab.label}
                    onClick={() => handleSubTabChange(tab)}
                    fontWeight={activeTab === tab ? 'semibold' : 'normal'}
                    bg={activeTab === tab ? 'blue.50' : undefined}
                  >
                    {tab.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          ) : (
            filteredTabs.map(tab => (
              <SingleItem.Tab
                key={tab.label}
                onClick={() => handleSubTabChange(tab)}
                isActive={activeTab === tab}
              >
                <Flex alignItems="center">
                  {tab.label}
                  {tab.hasTooltip && (
                    <Tooltip
                      hasArrow
                      shouldWrapChildren
                      placement="top"
                      label={tab.tooltip}
                      borderRadius={4}
                    >
                      <ButtonIcon
                        size="small"
                        ml={1}
                        ariaLabel="Help Icon"
                        icon={<FontAwesomeIcon icon={faInfoCircle} />}
                      />
                    </Tooltip>
                  )}
                </Flex>
              </SingleItem.Tab>
            ))
          )}
        </SingleItem.Navigation>

        <SingleItem.Content size={'small'}>
          {renderTabContent({
            activeTab,
            editorialAssignees,
            queryKey,
            submissionId
          })}
        </SingleItem.Content>
      </SingleItem.MainArea>
    </Flex>
  );
};

export default BackofficeTab;
